import React, { useState, useEffect, useRef, useCallback } from 'react';
import { isInNativeApp } from './platform';
import { collection, addDoc, query, orderBy, onSnapshot } from 'firebase/firestore';

// Flower types configuration
import CelestialFlowerArt from './components/CelestialFlowerArt';
import SwipeRail from './components/SwipeRail';

const FLOWER_TYPES = [
  { 
    id: 'hibiscus', 
    name: 'Hibiscus', 
    emoji: '🌺🌺🌺',
    image: '/assets/flowers/hibiscus.png',
    price: 0.00,
    color: 'rose' 
  },
  { 
    id: 'bouquet', 
    name: 'Bouquet', 
    emoji: '💐', 
    image: '/assets/flowers/bouquet.png',
    price: 0.00,
    color: 'pink' 
  },
  { 
    id: 'chrysanthemum', 
    name: 'Chrysanthemum', 
    emoji: '🕯️', 
    image: '/assets/flowers/chrysanthemum.png',
    price: 0.00,
    color: 'yellow' 
  },
  { 
    id: 'carnation', 
    name: 'Carnation', 
    emoji: '🌸', 
    image: '/assets/flowers/carnation.png',
    price: 0.00,
    color: 'purple' 
  },
];

// Predefined message options
const FLOWER_MESSAGE_OPTIONS = [
  { id: 'love', text: 'With love' },
  { id: 'memory', text: 'In loving memory' },
  { id: 'thinking', text: 'Thinking of you' },
  { id: 'hearts', text: 'Forever in our hearts' },
  { id: 'peace', text: 'Rest in peace' },
  { id: 'cherish', text: 'Cherishing the memories' },
  { id: 'miss', text: 'We miss you' },
  { id: 'grateful', text: 'Grateful for the memories' },
];

// Helper function to capitalize words
const capitalizeWords = (words) => {
  if (!words) return '';
  return words
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Helper function to get user display name
const getUserDisplayName = (user) => {
  if (!user) return 'Anonymous';
  return user.displayName || user.email?.split('@')[0] || 'Anonymous';
};

// Helper function to get flower details
const getFlowerDetails = (flowerType) => {
  return FLOWER_TYPES.find(f => f.id === flowerType) || FLOWER_TYPES[0];
};

// Helper function for flower placeholder text
const getFlowerPlaceholder = (flowerType, memorial) => {
  const firstName = memorial?.firstName ? capitalizeWords(memorial.firstName.trim()) : 'them';

  switch (flowerType?.id) {
    case 'hibiscus':
      return `This wreath is a circle of eternal love, honoring ${firstName}'s life and the lasting impact they leave behind. Each bloom is a reminder that their memory will never fade and their love continues to surround us with peace.`;
    case 'bouquet':
      return `These soft pink blossoms are a symbol of tenderness, compassion, and care. They are given in loving memory of ${firstName}, whose kindness continues to live on in every heart they touched.`;
    case 'chrysanthemum':
      return `These natural stems represent resilience and enduring beauty. May they serve as a tribute to ${firstName}'s strength, their rooted legacy, and the way their presence continues to inspire us, even as time moves forward.`;
    case 'carnation':
      return `This orchid represents elegance, grace, and enduring love. It is offered in remembrance of ${firstName}, whose presence is still felt by all who hold them dear.`;
    default:
      return "Share a memory or message...";
  }
};


// A small engraved sprig, the sort pressed at the foot of good stationery.
// Kept very pale so it sits *under* the words rather than competing with them.
function StemFlourish() {
  return (
    <svg viewBox="0 0 160 34" className="w-full h-full" aria-hidden="true">
      <g fill="none" stroke="#b8964f" strokeLinecap="round" strokeWidth="1">
        {/* the line the whole thing rests on, fading at both ends */}
        <path d="M8 24 H152" stroke="url(#flourishFade)" strokeWidth="0.8" />
        {/* stem */}
        <path d="M80 24 C80 16, 78 12, 74 8" />
        {/* leaves */}
        <path d="M74 9 C68 6, 62 8, 60 13 C66 15, 72 14, 74 9 Z" opacity="0.85" />
        <path d="M80 17 C86 14, 93 16, 95 21 C88 23, 82 22, 80 17 Z" opacity="0.85" />
        {/* seed head */}
        <circle cx="73.4" cy="7" r="1.6" fill="#b8964f" stroke="none" opacity="0.9" />
      </g>
      <defs>
        <linearGradient id="flourishFade" gradientUnits="userSpaceOnUse" x1="8" y1="24" x2="152" y2="24">
          <stop offset="0%" stopColor="#b8964f" stopOpacity="0" />
          <stop offset="50%" stopColor="#b8964f" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#b8964f" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export default function DigitalFlowersSection({ 
  memorial, 
  memorialId, 
  slug,
  auth, 
  db,
  handleLoginRedirect 
}) {
  const [flowers, setFlowers] = useState([]);
  const [flowerMessage, setFlowerMessage] = useState('');
  const [selectedFlowerType, setSelectedFlowerType] = useState(FLOWER_TYPES[3]);
  const [selectedFlowerMessage, setSelectedFlowerMessage] = useState(FLOWER_MESSAGE_OPTIONS[0]);
  // Flowers are keepsakes: each rests as a full postcard in a snap rail —
  // bloom on the left, words on the right, a gilded seam between them.
  const flowerRailRef = useRef(null);
  const [activeFlower, setActiveFlower] = useState(0);

  const handleFlowerScroll = useCallback(() => {
    const el = flowerRailRef.current;
    if (!el) return;
    const i = Math.round(el.scrollLeft / Math.max(el.clientWidth, 1));
    setActiveFlower((prev) => (prev === i ? prev : i));
  }, []);

  const scrollToFlower = useCallback((i) => {
    const el = flowerRailRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
    setActiveFlower(i);
  }, []);
  // Postcard behavior: flowers arrive closed; each opens on its own.

  // Subscribe to flowers
  useEffect(() => {
    if (!memorialId || !memorial) return;
    
    const q = query(
      collection(db, 'memorials', memorialId, 'flowers'),
      orderBy('timestamp', 'desc')
    );
    const unsubscribe = onSnapshot(q, snapshot => {
      setFlowers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, console.error);
    return () => unsubscribe();
  }, [memorialId, memorial, db]);

  // Handle payment success
  useEffect(() => {
    const handlePaymentSuccess = async () => {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get('session_id');
      
      if (sessionId && memorialId) {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/verifyFlowerPayment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId, memorialId })
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: Failed to verify payment`);
          }

          const data = await response.json();
          
          if (data.success) {
            const pendingFlower = sessionStorage.getItem('pendingFlower');
            
            if (pendingFlower) {
              const flowerData = JSON.parse(pendingFlower);
              
              await addDoc(collection(db, 'memorials', memorialId, 'flowers'), {
                userName: flowerData.userName,
                userId: auth.currentUser?.uid || 'anonymous',
                message: flowerData.message,
                flowerType: flowerData.flowerType,
                flowerMessageType: flowerData.flowerMessageType,
                timestamp: new Date(),
                sessionId: sessionId,
                verified: true,
                paid: true
              });
              
              setFlowerMessage('');
              setSelectedFlowerType(FLOWER_TYPES[3]);
              setSelectedFlowerMessage(FLOWER_MESSAGE_OPTIONS[0]);
              scrollToFlower(0);
              sessionStorage.removeItem('pendingFlower');
              
              const flowerName = FLOWER_TYPES.find(f => f.id === flowerData.flowerType)?.name || 'flower';
              const messageName = FLOWER_MESSAGE_OPTIONS.find(m => m.id === flowerData.flowerMessageType)?.text || 'With love';
              alert(`Thank you for your beautiful ${flowerName}! 🌸 Your "${messageName}" message will appear at the top of the memorial shortly.`);
              
              window.history.replaceState({}, document.title, window.location.pathname);
            }
          }
        } catch (error) {
          console.error('Error verifying payment:', error);
        }
      }
    };

    if (memorialId) {
      handlePaymentSuccess();
    }
  }, [memorialId, auth, db]);

  const handleGiveFlower = async () => {
    if (isInNativeApp()) {
      alert('Tributes are purchased on the web — please visit soulemap.com in your browser.');
      return;
    }
    if (!flowerMessage.trim()) {
      alert('Please write a message before giving a flower.');
      return;
    }

    if (selectedFlowerType.price === 0) {
      try {
        await addDoc(collection(db, 'memorials', memorialId, 'flowers'), {
          userName: auth.currentUser ? getUserDisplayName(auth.currentUser) : 'Anonymous',
          userId: auth.currentUser?.uid || 'anonymous',
          message: flowerMessage.trim(),
          flowerType: selectedFlowerType.id,
          flowerMessageType: selectedFlowerMessage.id,
          timestamp: new Date(),
          verified: true,
          paid: false,
        });
        
        setFlowerMessage('');
        setSelectedFlowerType(FLOWER_TYPES[3]);
        setSelectedFlowerMessage(FLOWER_MESSAGE_OPTIONS[0]);
        scrollToFlower(0);
        
        alert(`Thank you for your beautiful ${selectedFlowerType.name}! 🌸 Your "${selectedFlowerMessage.text}" message will appear at the top of the memorial shortly.`);
      } catch (error) {
        console.error('Error placing free flower:', error);
        alert('Error placing flower. Please try again.');
      }
      return;
    }

    if (!window.Stripe) {
      alert('Stripe.js not loaded. Please refresh the page and try again.');
      return;
    }

    try {
      const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
      
      if (!stripe) {
        throw new Error('Stripe not initialized properly');
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/createFlowerSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          memorialId: slug,
          message: flowerMessage.trim(),
          flowerType: selectedFlowerType.id,
          price: selectedFlowerType.price,
          customerEmail: auth.currentUser?.email || 'anonymous@flower.com',
          userName: auth.currentUser ? getUserDisplayName(auth.currentUser) : 'Anonymous'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: Failed to create payment session`);
      }

      const { sessionId } = await response.json();
      
      if (!sessionId) {
        throw new Error('No session ID received from server');
      }
      
      sessionStorage.setItem('pendingFlower', JSON.stringify({
        message: flowerMessage.trim(),
        flowerType: selectedFlowerType.id,
        flowerMessageType: selectedFlowerMessage.id,
        userName: auth.currentUser ? getUserDisplayName(auth.currentUser) : 'Anonymous',
        memorialId: memorialId,
        timestamp: new Date().toISOString()
      }));
      
      const { error } = await stripe.redirectToCheckout({ sessionId });
      
      if (error) {
        console.error('Stripe redirect error:', error);
        sessionStorage.removeItem('pendingFlower');
        alert(`Payment failed: ${error.message}`);
      }
    } catch (err) {
      console.error('Flower purchase error:', err);
      alert(`Error: ${err.message}`);
      sessionStorage.removeItem('pendingFlower');
    }
  };

  return (
    <div id="flowers-section" className="bg-white p-6 md:p-10 mb-8 border border-[#eee9df]">
      <div className="text-center mb-8">
        <p className="text-[10.5px] uppercase" style={{ letterSpacing: '0.34em', color: '#b8964f' }}>
          Digital Flower Cards
        </p>
        <h2 className="mt-3 text-2xl md:text-3xl font-extralight text-gray-900" style={{ letterSpacing: '0.04em' }}>
          A Flower Tribute
        </h2>
        <div
          className="mx-auto mt-4"
          style={{ width: '54px', height: '1px', background: 'linear-gradient(90deg, transparent, #cbab6a, transparent)' }}
        />
        <p className="mt-4 mb-6 text-gray-500 text-sm font-light">
          Laid in {memorial?.firstName ? `${memorial.firstName}'s` : 'their'} memory
        </p>

        {/* Flowers list */}
        <div className="space-y-6">
          {flowers.length > 0 && (
          <h3 className="text-lg font-extralight text-gray-700 text-center mb-6" style={{ letterSpacing: '0.08em' }}>
            {flowers.length} Flower{flowers.length !== 1 ? "s" : ""} Placed
          </h3>
          )}

          {flowers.length > 0 ? (
            <div className="max-w-4xl mx-auto">
            <div
              ref={flowerRailRef}
              onScroll={handleFlowerScroll}
              className="rail flex gap-4 overflow-x-auto pb-1"
              style={{
                scrollSnapType: 'x mandatory',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {flowers.map((flower, idx) => {
                const flowerDetails = getFlowerDetails(flower.flowerType);
                const messageText = FLOWER_MESSAGE_OPTIONS.find(m => m.id === flower.flowerMessageType)?.text || flower.flowerMessageType || "With love";
                const displayName = flower.userName || "Anonymous";
                const cardKey = flower.id || `flower-${idx}`;
                const dateStr = flower.timestamp?.toDate
                  ? flower.timestamp.toDate().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
                  : new Date(flower.timestamp).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

                return (
                  <div
                    key={cardKey}
                    className="relative overflow-hidden rounded-2xl border border-[#eee9df] bg-white shadow-lg"
                    style={{ flex: '0 0 100%', scrollSnapAlign: 'center' }}
                  >
                    <div className="flex items-stretch h-[17rem] sm:h-[19rem] md:h-[22rem]">
                      {/* The bloom — always on the left, on every screen */}
                      <div className="relative overflow-hidden shrink-0 h-full" style={{ width: '42%' }}>
                        <div className="absolute inset-0">
                          <CelestialFlowerArt type={flowerDetails.id} name={flowerDetails.name} />
                        </div>
                      </div>

                      {/* Where the picture meets the paper: no line at all —
                          the parchment simply dissolves into the page. */}
                      <div
                        className="relative shrink-0 h-full"
                        style={{
                          width: '46px',
                          marginLeft: '-46px',
                          pointerEvents: 'none',
                          background: 'linear-gradient(90deg, rgba(253,251,246,0) 0%, rgba(253,251,246,0.75) 45%, #ffffff 100%)',
                        }}
                      />

                      {/* The words — always on the right */}
                      <div className="flex-1 min-w-0 h-full bg-white px-4 py-5 md:px-8 md:py-7 flex flex-col justify-center text-center overflow-y-auto">
                        <p className="text-[9px] md:text-[10px] uppercase mb-2" style={{ letterSpacing: '0.24em', color: '#b8964f' }}>
                          {flowerDetails.name}
                        </p>
                        <h4
                          className="text-base md:text-2xl font-extralight leading-snug break-words"
                          style={{ color: '#96793c', letterSpacing: '0.02em' }}
                        >
                          {messageText}
                        </h4>

                        {flower.message && (
                          <p className="mt-3 text-stone-700 italic leading-relaxed break-words text-[13px] md:text-base">
                            &ldquo;{flower.message}&rdquo;
                          </p>
                        )}

                        <div className="mt-5">
                          <div
                            className="mx-auto mb-3"
                            style={{ width: '48px', height: '1px', background: 'linear-gradient(90deg, transparent, #cbab6a, transparent)' }}
                          />
                          <p className="text-sm md:text-base text-stone-700/90 font-light">
                            From {displayName}
                          </p>
                          <p className="text-[11px] md:text-sm text-stone-500 mt-0.5">{dateStr}</p>
                        </div>

                        {/* the engraving at the foot of the page */}
                        <div
                          className="mx-auto mt-4 pointer-events-none"
                          style={{ width: '68%', maxWidth: '190px', height: '26px', opacity: 0.5 }}
                        >
                          <StemFlourish />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

            </div>

            {/* Which flower you're looking at — and a way to reach any of them */}
            {flowers.length > 1 && (
              <div className="mt-4 text-center">
                <div className="flex items-center justify-center gap-1.5 flex-wrap">
                  {flowers.map((f, i) => (
                    <button
                      key={`fdot-${f.id || i}`}
                      onClick={() => scrollToFlower(i)}
                      aria-label={`Flower ${i + 1} of ${flowers.length}`}
                      className="p-1 leading-none"
                      style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      <span
                        className="block rounded-full"
                        style={{
                          width: i === activeFlower ? '7px' : '5px',
                          height: i === activeFlower ? '7px' : '5px',
                          background: i === activeFlower ? '#b8964f' : 'rgba(184,150,79,0.3)',
                          transition: 'all 350ms ease',
                        }}
                      />
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-[11px] uppercase text-gray-400" style={{ letterSpacing: '0.22em' }}>
                  {activeFlower + 1} of {flowers.length} · swipe to see them all
                </p>
              </div>
            )}
            </div>
          ) : (
            <div className="max-w-md mx-auto text-center py-8">
              <div
                className="w-14 h-14 mx-auto mb-5 rounded-full flex items-center justify-center text-xl"
                style={{ background: 'radial-gradient(circle at 35% 30%, #2a3152, #0d1020)', boxShadow: '0 0 0 1px rgba(255,215,140,0.5), 0 0 20px rgba(255,215,140,0.25)' }}
              >
                🌹
              </div>
              <p className="text-gray-500 text-sm font-light">
                No flowers rest here yet — {auth.currentUser ? 'yours could be the first.' : 'sign in to leave the first one.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Flower placement form */}
      {auth.currentUser ? (
        <div className="max-w-3xl mx-auto mb-8">
          {/* Flower type chooser */}
          <div className="mb-6">
            <p className="text-gray-600 text-lg font-light pb-4">
              Choose a flower, select your message, and share your memories
            </p>
            <SwipeRail>
              {FLOWER_TYPES.map((flower) => (
                <button
                  key={flower.id}
                  onClick={() => setSelectedFlowerType(flower)}
                  style={{ scrollSnapAlign: 'start' }}
                  className={`relative shrink-0 basis-[46%] sm:basis-[31%] md:basis-[23%] border-2 rounded-xl transition-all duration-300 overflow-hidden min-h-[120px] md:min-h-[140px] group ${
                    selectedFlowerType.id === flower.id
                      ? "border-[#cbab6a] shadow-xl ring-2 ring-[#e5c883]"
                      : "border-[#eee9df] hover:border-[#d8c9a4] hover:shadow-lg"
                  }`}
                >
                  <div className="absolute inset-0">
                    <CelestialFlowerArt type={flower.id} compact />
                  </div>
                  <div className="absolute bottom-2 left-0 right-0 text-center pointer-events-none">
                    <span className="text-[9px] uppercase" style={{ letterSpacing: '0.22em', color: '#b8964f' }}>
                      {flower.name}
                    </span>
                  </div>
                  <div className="relative z-10 flex flex-col items-center justify-end h-full">
                    <div className="text-center">
                      {flower.price > 0 && (
                        <div className="text-xs font-light text-white bg-black backdrop-blur-sm px-3 py-1 rounded-full"
                          style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.6)' }}>
                          ${flower.price}
                        </div>
                      )}
                    </div>
                  </div>
                  {selectedFlowerType.id === flower.id && (
                    <div className="absolute top-2 right-2 z-20">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #e5c883, #b8964f)' }}>
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}
                </button>
              ))}
            </SwipeRail>
          </div>

          {/* Message preset */}
          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-3">
              Choose Your Message
            </label>
            <select
              value={selectedFlowerMessage.id}
              onChange={(e) => {
                const selectedOption = FLOWER_MESSAGE_OPTIONS.find(option => option.id === e.target.value);
                setSelectedFlowerMessage(selectedOption);
              }}
              className="w-full px-4 py-3 bg-[#faf9f7] border border-[#eae6dd] focus:outline-none focus:border-[#cbab6a] text-gray-700 transition duration-300 rounded-xl"
            >
              {FLOWER_MESSAGE_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.text}
                </option>
              ))}
            </select>
          </div>

          {/* Personal message */}
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-3">
              Your Personal Message
            </label>
            <textarea
              rows={8}
              placeholder={getFlowerPlaceholder(selectedFlowerType, memorial)}
              value={flowerMessage}
              onChange={(e) => setFlowerMessage(e.target.value)}
              className="w-full px-6 py-4 bg-[#faf9f7] border border-[#eae6dd] focus:outline-none focus:border-[#cbab6a] focus:shadow-[0_0_18px_rgba(255,215,140,0.15)] resize-none text-gray-700 placeholder-gray-400 transition duration-300 rounded-xl text-base md:text-sm leading-relaxed"
              style={{ fontFamily: 'Garamond, serif', fontSize: '16px' }}
            />
            <p className="text-xs text-gray-500 mt-2">
              💡 Tip: A template message is pre-filled above. You can edit it or write your own personal message from the heart
            </p>
          </div>

          <div className="flex justify-center mt-6">
            <button
              onClick={handleGiveFlower}
              disabled={!flowerMessage.trim()}
              className="sm-btn-primary"
            >
              Place a Virtual {selectedFlowerType.name} {selectedFlowerType.price > 0 ? `($${selectedFlowerType.price})` : ''}
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center mb-8 bg-[#faf9f7] border border-[#eee9df] p-8 max-w-3xl mx-auto rounded-2xl">
          <div
            className="w-14 h-14 mx-auto mb-5 rounded-full flex items-center justify-center text-xl"
            style={{ background: 'radial-gradient(circle at 35% 30%, #2a3152, #0d1020)', boxShadow: '0 0 0 1px rgba(255,215,140,0.5), 0 0 20px rgba(255,215,140,0.25)' }}
          >
            🌹
          </div>
          <p className="text-gray-600 mb-4 text-lg font-light">
            Sign in to share memories and leave flowers on this memorial
          </p>
          <p className="text-gray-500 mb-6 text-sm">
            Honor their memory with a beautiful digital flower and personal message
          </p>
          <button
            onClick={handleLoginRedirect}
            className="sm-btn-primary"
          >
            Log In to Place Flower
          </button>
        </div>
      )}
    </div>
  );
}